"use client";

import { useState, useEffect, useMemo, useRef } from "react";
import Link from "next/link";
import { useCart } from "../contexts/CartContext";
import {
  generateProductSlug,
  cleanProductName,
  extractBrandName,
} from "@shared/lib/slugify";
import { parseProduct } from "@shared/lib/product-parser";
import { extractVariants, extractColorsAndSizes, getColorHex } from "@/lib/variant-extractor";
import { PRODUCT_PLACEHOLDER_IMAGE } from "@shared/lib/product-placeholder";
import FlameFilledIcon from "../icons/FlameFilledIcon";
import FavoriteButton from "./FavoriteButton";
import Button from "./Button";
import ChevronLeftIcon from '../icons/ChevronLeftIcon';
import ChevronRightIcon from '../icons/ChevronRightIcon';
import {
  resolveSellingPriceFromAvailability,
  strikethroughCompareAtPrice,
} from "@/lib/storefront-pricing";
import {
  getCartValueBucket,
  trackFunnelEvent,
  trackFunnelEventOnce,
} from "@/lib/analytics/funnel";
import { mergeCannabisFieldsFromAvailability } from "@/lib/product-potency-merge";

type SizeOption = {
  packageId: string;
  sizeOrWeight: string | null;
  netContent?: string | null; // Legacy support
  price: number;
  compareAtPrice?: number | null; // “Was” anchor, use with selling price, not unitCost
  unitCost?: number | null; // Store acquisition (COGS); not for promo strikethrough
  inStock: number;
  productId: string;
  variationId?: string | null;
  productSlug?: string | null; // Product slug for navigation (FIFO)
  heroShotUri?: string | null; // Variant-specific image
  longDescription?: string | null; // Variant-specific description
  shortDescription?: string | null; // Variant-specific short description
};

type ProductAvailability = {
  id: string;
  productId: string;
  covaLocationId: number;
  packageId: string;
  inStock: number;
  unitCost: number;
  compareAtPrice?: number | null; // Variant MSRP anchor for discount UI
  lotCode?: string | null; // Set when the product shows each lot as its own card (split-lots mode)
  sizeOptions?: SizeOption[]; // Size/weight options for grouped products
  brand?: {
    name: string;
    logoUri?: string | null;
    isSponsored?: boolean;
    sponsoredBadgeText?: string | null;
  };
  product: {
    id: string;
    name: string;
    masterProductName?: string | null;
    catalogSku?: string;
    vendorSku?: string;
    classificationName?: string;
    slug?: string;
    shortDescription?: string;
    longDescription?: string;
    heroShotUri?: string;
    strain?: string;
    brandName?: string;
    thcMin?: number;
    thcMax?: number;
    thcUom?: string;
    thcPercent?: number;
    thcMg?: number;
    cbdMin?: number;
    cbdMax?: number;
    cbdUom?: string;
    cbdPercent?: number;
    cbdMg?: number;
    netContent?: string;
    pieces?: number;
    equivalenceDriedFlowerG?: number;
    specifications?: { name: string; value: string }[];
    /** Store availabilities for package-level potency (merged when rendering). */
    availabilities?: Array<Record<string, unknown>>;
  };
};

type ProductCardProps = {
  item: ProductAvailability;
  price: number;
  onQuickView?: (item: ProductAvailability) => void;
  homepage?: boolean;
  relatedProducts?: boolean; // Flag for related products section
  /** When set, used as `source` on funnel_product_click / funnel_add_to_cart (issue #43). */
  analyticsSource?: string;
};

export default function ProductCard({
  item,
  price,
  onQuickView,
  homepage = false,
  relatedProducts = false,
  analyticsSource,
}: ProductCardProps) {
  const { addItem, state: cartState } = useCart();
  const [selectedQuantity, setSelectedQuantity] = useState<string>("1");
  const [imageError, setImageError] = useState(false);
  const [placeholderError, setPlaceholderError] = useState(false);
  const [isColorDropdownOpen, setIsColorDropdownOpen] = useState(false);
  const sizeScrollRef = useRef<HTMLDivElement | null>(null);
  const [canScrollSizesLeft, setCanScrollSizesLeft] = useState(false);
  const [canScrollSizesRight, setCanScrollSizesRight] = useState(false);
  // Filter to only show available size options (inStock >= 1)
  const availableSizeOptions = item.sizeOptions
    ? item.sizeOptions.filter((opt) => opt.inStock >= 1)
    : [];

  const [selectedSizeOption, setSelectedSizeOption] =
    useState<SizeOption | null>(
      availableSizeOptions.length > 0 ? availableSizeOptions[0] : null
    );

  /** Variant + store availability overrides for cannabinoids / strain (matches PDP). */
  const potencyProduct = useMemo(() => {
    let p = item.product;
    const groupProducts = (item as { groupProducts?: any[] }).groupProducts;
    const so = selectedSizeOption;
    if (groupProducts?.length && so) {
      const gp = groupProducts.find(
        (g: any) =>
          (so.productId && g.productId === so.productId) ||
          (so.packageId &&
            (g.packageId === so.packageId ||
              g.product?.packageId === so.packageId))
      );
      const vp = gp?.product ?? gp;
      if (vp && typeof vp === "object") {
        p = mergeCannabisFieldsFromAvailability(p, vp);
      }
    }
    const avails = item.product.availabilities as
      | Array<Record<string, unknown>>
      | undefined;
    const locId = cartState.storeCovaLocationId;
    if (avails?.length && locId) {
      const avail =
        avails.find(
          (a: any) =>
            Number(a.covaLocationId) === locId &&
            (!so?.packageId ||
              a.packageId === so.packageId ||
              a.packageId === item.packageId)
        ) ?? avails.find((a: any) => Number(a.covaLocationId) === locId);
      if (avail) {
        p = mergeCannabisFieldsFromAvailability(p, avail);
      }
    }
    return p;
  }, [
    item.product,
    item.packageId,
    (item as { groupProducts?: unknown }).groupProducts,
    selectedSizeOption,
    cartState.storeCovaLocationId,
  ]);
  
  // Track selected color for clothing products
  const [selectedColor, setSelectedColor] = useState<string | null>(null);
  
  // getColorHex is now imported from @/lib/variant-extractor

  // Organize clothing variants by color for better UX (using same logic as admin panel)
  const clothingVariants = useMemo(() => {
    const classification = item.product.classificationName || '';
    const isAccessories = classification && classification.toLowerCase().includes('accessories');
    const isClothing = classification && (
      classification.toLowerCase().includes('clothing') ||
      classification.toLowerCase().includes('merch') ||
      classification.toLowerCase().includes('apparel')
    ) && !isAccessories; // Exclude accessories from clothing
    
    // Use groupProducts if available (contains ALL products in the group), otherwise fallback to availableSizeOptions
    const groupProducts = (item as any).groupProducts || [];
    
    if (!isClothing || (!groupProducts.length && availableSizeOptions.length === 0)) {
      return null;
    }
    
    // Build a map of all products with their variants (matching admin panel logic)
    // Use groupProducts if available (contains all products with original names), otherwise use availableSizeOptions
    const allProducts: Array<{
      productId: string;
      packageId: string;
      productSlug: string | null;
      originalProductName: string;
      sizeOrWeight: string | null;
      inStock: number;
      price: number;
      heroShotUri: string | null;
      color: string | null;
      size: string | null;
    }> = [];
    
    if (groupProducts.length > 0) {
      // Use groupProducts (contains ALL products in the group with original product names)
      for (const gp of groupProducts) {
        const originalProductName = (gp as any).product?.name || gp.productId || '';
        const sizeOrWeight = (gp as any).sizeOrWeight || null;
        
        // Extract variants from original product name (matching admin panel)
        const variants = extractVariants(
          originalProductName,
          item.product.classificationName || null,
          (gp as any).product?.brandName || item.product.brandName || null,
          sizeOrWeight
        );
        
        // Find corresponding sizeOption for this groupProduct to get price, inStock, etc.
        const matchingSizeOption = availableSizeOptions.find(
          (opt: any) => opt.productId === gp.productId || opt.packageId === gp.packageId
        );
        
        allProducts.push({
          productId: gp.productId,
          packageId: gp.packageId || '',
          productSlug: matchingSizeOption?.productSlug || null,
          originalProductName,
          sizeOrWeight,
          inStock: matchingSizeOption?.inStock ?? gp.inStock ?? 0,
          price: matchingSizeOption?.price ?? gp.price ?? gp.unitCost ?? 0,
          heroShotUri: matchingSizeOption?.heroShotUri || (gp as any).product?.heroShotUri || null,
          color: variants.color,
          size: variants.size,
        });
      }
    } else if (availableSizeOptions.length > 0) {
      // Fallback to availableSizeOptions if groupProducts not available
      for (const option of availableSizeOptions) {
        let originalProductName = (option as any).originalProductName || item.product.name;
        
        // Extract variants
        const variants = extractVariants(
          originalProductName,
          item.product.classificationName || null,
          item.product.brandName || null,
          option.sizeOrWeight || null
        );
        
        allProducts.push({
          productId: option.productId,
          packageId: option.packageId,
          productSlug: option.productSlug || null,
          originalProductName,
          sizeOrWeight: option.sizeOrWeight || null,
          inStock: option.inStock || 0,
          price: option.price || 0,
          heroShotUri: option.heroShotUri || null,
          color: variants.color,
          size: variants.size,
        });
      }
    }
    
    if (allProducts.length === 0) {
      return null;
    }
    
    // Count variant types to determine primary grouping (matching admin panel)
    const variantTypeCounts = {
      color: 0,
      size: 0,
      weight: 0,
    };
    
    allProducts.forEach(p => {
      if (p.color) variantTypeCounts.color++;
      if (p.size) variantTypeCounts.size++;
    });
    
    const hasAnyColor = variantTypeCounts.color > 0;
    
    // Determine primary grouping: color if most common (matching admin panel)
    const primaryGrouping: 'color' | 'size' | null =
      variantTypeCounts.color >= variantTypeCounts.size && hasAnyColor ? 'color' : null;
    
    // Group by primary variant (color for clothing)
    const variantsByColor = new Map<string, typeof allProducts>();
    const colorsSet = new Set<string>();
    
    if (primaryGrouping === 'color') {
      // Group by color first (matching admin panel)
      allProducts.forEach(product => {
        const color = product.color || 'Default';
        colorsSet.add(color);
        
        if (!variantsByColor.has(color)) {
          variantsByColor.set(color, []);
        }
        variantsByColor.get(color)!.push(product);
      });
    } else {
      // No color grouping, create a single group
      variantsByColor.set('Default', allProducts);
      colorsSet.add('Default');
    }
    
    const colors = Array.from(colorsSet).sort();
    
    // Only return if we have multiple colors (for clothing)
    if (primaryGrouping === 'color' && colors.length <= 1) {
      return null;
    }
    
    return {
      colors,
      variantsByColor,
      allProducts,
      primaryGrouping,
    };
  }, [availableSizeOptions, item]);

  // Initialize selected color and size based on displayed product when clothingVariants changes
  useEffect(() => {
    if (clothingVariants && selectedColor === null && clothingVariants.colors.length > 0) {
      // Get groupProducts for raw stock values
      const groupProducts = (item as any).groupProducts || [];
      
      // Find the color of the displayed product (item) by matching productId/packageId
      let displayedProductColor: string | null = null;
      for (const [color, colorProducts] of clothingVariants.variantsByColor.entries()) {
        const matchesDisplayedProduct = colorProducts.some(p => 
          (p.productId === item.productId && item.productId) ||
          (p.packageId === item.packageId && item.packageId)
        );
        if (matchesDisplayedProduct) {
          displayedProductColor = color;
          break;
        }
      }
      
      // Filter to only available colors (with stock >= 2, so stock-1 >= 1)
      // Also check if there are any size options available for that color
      const availableColors = clothingVariants.colors.filter((color) => {
        const colorProducts = clothingVariants.variantsByColor.get(color) || [];
        
        // Create a Set of productIds and packageIds for this color
        const colorProductIds = new Set<string>();
        const colorPackageIds = new Set<string>();
        colorProducts.forEach(p => {
          if (p.productId) colorProductIds.add(p.productId);
          if (p.packageId) colorPackageIds.add(p.packageId);
        });
        
        // Check if any size options match this color's products
        const hasMatchingSizeOptions = availableSizeOptions.some((opt: SizeOption) =>
          (opt.productId && colorProductIds.has(opt.productId)) ||
          (opt.packageId && colorPackageIds.has(opt.packageId))
        );
        
        // If no matching size options, hide this color
        if (!hasMatchingSizeOptions) return false;
        
        // Check if any product has stock >= 2 (so stock-1 >= 1)
        return colorProducts.some(p => {
          // Get raw stock from groupProducts if available, otherwise use p.inStock
          const rawStock = groupProducts.find((gp: any) => 
            gp.productId === p.productId || gp.packageId === p.packageId
          )?.inStock ?? p.inStock ?? 0;
          
          // Apply stock-1 rule: if raw stock is 2, available stock is 1
          const displayStock = Math.max(0, rawStock - 1);
          return displayStock > 0;
        });
      });
      
      // Select the displayed product's color if available, otherwise select first available color
      const colorToSelect = displayedProductColor && availableColors.includes(displayedProductColor)
        ? displayedProductColor
        : availableColors[0];
      
      if (colorToSelect) {
        setSelectedColor(colorToSelect);
      }
      
      // Find and select the size option that matches the displayed product
      const matchingSizeOption = availableSizeOptions.find((opt: SizeOption) =>
        (opt.productId === item.productId && item.productId) ||
        (opt.packageId === item.packageId && item.packageId)
      );
      
      if (matchingSizeOption) {
        setSelectedSizeOption(matchingSizeOption);
      }
    }
  }, [clothingVariants, selectedColor, item, availableSizeOptions]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isColorDropdownOpen) return;
    
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.color-dropdown-container')) {
        setIsColorDropdownOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isColorDropdownOpen]);

  // Helper function to normalize size string for comparison
  const normalizeSize = (size: string | null | undefined): string => {
    if (!size) return 'Default';
    return String(size).trim().toLowerCase();
  };

  // Display size options - for clothing with color selection, filter by selected color and show all available sizes
  const displaySizeOptions = useMemo(() => {
    // Always deduplicate by normalized size to prevent duplicates
    const sizeToOptionMap = new Map<string, SizeOption>();
    
    if (clothingVariants && selectedColor && clothingVariants.colors.length > 0) {
      // Get all products for the selected color (these have productId/packageId we can match)
      const colorProducts = clothingVariants.variantsByColor.get(selectedColor) || [];
      
      // Create a Set of productIds and packageIds for fast lookup
      const colorProductIds = new Set<string>();
      const colorPackageIds = new Set<string>();
      colorProducts.forEach(p => {
        if (p.productId) colorProductIds.add(p.productId);
        if (p.packageId) colorPackageIds.add(p.packageId);
      });
      
      // Filter availableSizeOptions to only those matching the selected color's products
      // Then deduplicate by normalized size
      availableSizeOptions.forEach((opt: SizeOption) => {
        // Check if this sizeOption matches any product in the selected color
        const matchesColor = 
          (opt.productId && colorProductIds.has(opt.productId)) ||
          (opt.packageId && colorPackageIds.has(opt.packageId));
        
        if (matchesColor) {
          // Use sizeOrWeight as the source of truth (contains actual sizes like "XL", "L", "M", etc.)
          const rawSize = opt.sizeOrWeight || opt.netContent || 'Default';
          const normalizedSize = normalizeSize(rawSize);
          
          // Only keep the first option for each normalized size (ensures ONE button per unique size)
          if (!sizeToOptionMap.has(normalizedSize)) {
            sizeToOptionMap.set(normalizedSize, opt);
          }
        }
      });
    } else {
      // For non-clothing or when no color is selected, deduplicate by normalized size
      availableSizeOptions.forEach((opt: SizeOption) => {
        const rawSize = opt.sizeOrWeight || opt.netContent || 'Default';
        const normalizedSize = normalizeSize(rawSize);
        // Only keep the first option for each normalized size
        if (!sizeToOptionMap.has(normalizedSize)) {
          sizeToOptionMap.set(normalizedSize, opt);
        }
      });
    }
    
    // Sort sizes and return corresponding sizeOptions
    // Use the original sizeOrWeight for sorting (not normalized)
    const sortedSizes = Array.from(sizeToOptionMap.entries()).sort(([sizeA, optA], [sizeB, optB]) => {
      // Use the display size (sizeOrWeight) for sorting
      const displaySizeA = optA.sizeOrWeight || optA.netContent || sizeA;
      const displaySizeB = optB.sizeOrWeight || optB.netContent || sizeB;
      
      // Sort sizes: numeric first, then alphabetical
      const aNum = parseFloat(String(displaySizeA).replace(/[^0-9.]/g, '')) || 0;
      const bNum = parseFloat(String(displaySizeB).replace(/[^0-9.]/g, '')) || 0;
      if (aNum !== 0 || bNum !== 0) {
        return aNum - bNum;
      }
      // Size order: XS, S, M, L, XL, XXL, etc.
      const sizeOrder = ['xs', 's', 'm', 'l', 'xl', 'xxl', 'xxxl', '2xl', '3xl', '4xl', 'small', 'medium', 'large', 'extra small', 'extra large', 'one size'];
      const aLower = String(displaySizeA).toLowerCase();
      const bLower = String(displaySizeB).toLowerCase();
      const aIndex = sizeOrder.findIndex(size => aLower.includes(size));
      const bIndex = sizeOrder.findIndex(size => bLower.includes(size));
      if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      return aLower.localeCompare(bLower);
    });
    
    // Return sizeOptions in sorted order, ensuring one per unique normalized size
    return sortedSizes.map(([_, opt]) => opt).filter(Boolean);
  }, [availableSizeOptions, clothingVariants, selectedColor]);

  useEffect(() => {
    const el = sizeScrollRef.current;
    if (!el) return;

    const update = () => {
      const maxLeft = el.scrollWidth - el.clientWidth;
      setCanScrollSizesLeft(el.scrollLeft > 2);
      setCanScrollSizesRight(maxLeft - el.scrollLeft > 2);
    };

    update();
    el.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      el.removeEventListener('scroll', update as any);
      window.removeEventListener('resize', update);
    };
  }, [displaySizeOptions.length]);

  // Track previous selectedColor to detect color changes
  const prevSelectedColorRef = useRef<string | null>(null);

  // Update selected size option when color changes for clothing products
  // Always auto-select the first available size option for the selected color
  useEffect(() => {
    if (clothingVariants && selectedColor && displaySizeOptions.length > 0) {
      // Only auto-select if color actually changed (not on initial load)
      const colorChanged = prevSelectedColorRef.current !== null && prevSelectedColorRef.current !== selectedColor;
      
      if (colorChanged) {
        // When color changes, always select the first size option for that color
        const firstSizeOption = displaySizeOptions[0];
        if (firstSizeOption) {
          setSelectedSizeOption(firstSizeOption);
        }
      }
      
      // Update the ref to track the current color
      prevSelectedColorRef.current = selectedColor;
    }
  }, [selectedColor, clothingVariants, displaySizeOptions]);

  // Selling price: Availability.price (surfaced as option.price / card price prop), not acquisition cost
  const displayPrice = selectedSizeOption
    ? resolveSellingPriceFromAvailability(
        selectedSizeOption.price,
        Number((selectedSizeOption as any).unitCost ?? 0)
      )
    : resolveSellingPriceFromAvailability(
        (item as any).price ?? price,
        item.unitCost
      );
  const displayInStock = selectedSizeOption
    ? selectedSizeOption.inStock
    : item.inStock;
  const displayPackageId = selectedSizeOption
    ? selectedSizeOption.packageId
    : item.packageId;
  const displayProductId = selectedSizeOption
    ? selectedSizeOption.productId
    : item.productId;
  
  // Use variant-specific heroShotUri if available, otherwise use product's default
  // For clothing with selected color, use the selected size option's heroShotUri (which is the first size option for that color)
  // Priority: selectedSizeOption.heroShotUri > first size option for selected color > product default
  const displayHeroShotUri = useMemo(() => {
    // If we have a selected size option, use its heroShotUri (this will be the first size option for the selected color)
    if (selectedSizeOption?.heroShotUri) {
      return selectedSizeOption.heroShotUri;
    }
    
    // If color is selected but no size option yet, get the first size option for that color
    if (clothingVariants && selectedColor && displaySizeOptions.length > 0) {
      const firstSizeOption = displaySizeOptions[0];
      if (firstSizeOption?.heroShotUri) {
        return firstSizeOption.heroShotUri;
      }
    }
    
    // Fallback to product's default heroShotUri
    return item.product.heroShotUri;
  }, [selectedSizeOption, clothingVariants, selectedColor, displaySizeOptions, item.product.heroShotUri]);

  // Strikethrough: compare-at above selling price only (never unitCost vs price)
  const getOriginalPrice = (): number | null => {
    if (selectedSizeOption) {
      return strikethroughCompareAtPrice(
        displayPrice,
        selectedSizeOption.compareAtPrice
      );
    }
    return strikethroughCompareAtPrice(displayPrice, item.compareAtPrice);
  };

  const originalPrice = getOriginalPrice();

  // Reset error states when image URL changes
  useEffect(() => {
    setImageError(false);
    setPlaceholderError(false);
  }, [displayHeroShotUri]);

  // Extract product attributes (potency / strain follow selected variant + package when possible)
  const strain = potencyProduct.strain || "";
  // Use parsed product name for brand extraction (item.product.name is already the parsed Product Name from backend)
  const productNameForBrand = item.product.name;
  // Detect brand from name if brandName is not provided
  const brand =
    extractBrandName(productNameForBrand, item.product.brandName) || "";

  /** Title without duplicated brand/size noise; single-line ellipsis in the UI */
  const displayProductTitle = useMemo(() => {
    const cleaned = cleanProductName(item.product.name, item.product.brandName);
    return cleaned?.trim() ? cleaned.trim() : item.product.name;
  }, [item.product.name, item.product.brandName]);

  /** PDP URL, shared by image and title links (keyboard + SR semantics). */
  const productHref = useMemo(() => {
    const displayName = item.product.name;
    const productNameSlug = generateProductSlug({
      name: displayName,
      id: item.productId,
      brandName: item.product.brandName,
      classificationName: item.product.classificationName,
      sizeOrWeight: null,
    });
    const productSlug = selectedSizeOption?.productSlug || item.product.slug;
    const params = new URLSearchParams();
    if (productSlug) {
      params.set("variant", productSlug);
    }
    const queryString = params.toString();
    return `/product/${productNameSlug}${queryString ? `?${queryString}` : ""}`;
  }, [
    item.productId,
    item.product.name,
    item.product.brandName,
    item.product.classificationName,
    item.product.slug,
    selectedSizeOption?.productSlug,
  ]);

  // Product compare feature removed.

  // Define classification here (used below for product type determination)
  const classification = item.product.classificationName || "";
  const thcMin = potencyProduct.thcMin;
  const thcMax = potencyProduct.thcMax;
  const thcUom = potencyProduct.thcUom || "";
  const thcPercent = potencyProduct.thcPercent;
  const thcMg = potencyProduct.thcMg;
  const cbdMin = potencyProduct.cbdMin;
  const cbdMax = potencyProduct.cbdMax;
  const cbdUom = potencyProduct.cbdUom || "";
  const cbdPercent = potencyProduct.cbdPercent;
  const cbdMg = potencyProduct.cbdMg;
  const netContent = potencyProduct.netContent || "";
  const pieces = potencyProduct.pieces;
  const equivalenceDriedFlowerG = potencyProduct.equivalenceDriedFlowerG;

  // Determine product type from classification or specifications
  const getProductType = () => {
    if (classification) {
      const lower = classification.toLowerCase();
      if (lower.includes("pre-roll") || lower.includes("preroll"))
        return "Pre-Roll";
      if (lower.includes("flower")) return "Flower";
      if (lower.includes("vape") || lower.includes("cartridge")) return "Vapes";
      if (lower.includes("edible")) return "Edibles";
      if (lower.includes("concentrate")) return "Concentrates";
    }
    return classification || "Cannabis";
  };

  const productType = getProductType();

  // Get strain type (Sativa, Indica, Hybrid) - Improved detection
  const getStrainType = () => {
    if (!strain) return "";

    const lower = strain.toLowerCase().trim();

    // Direct matches for strain type keywords
    if (lower === "sativa" || lower === "indica" || lower === "hybrid") {
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    }

    // Check if strain name contains type keywords
    if (
      lower.includes("sativa") &&
      !lower.includes("indica") &&
      !lower.includes("hybrid")
    ) {
      return "Sativa";
    }
    if (
      lower.includes("indica") &&
      !lower.includes("sativa") &&
      !lower.includes("hybrid")
    ) {
      return "Indica";
    }
    if (lower.includes("hybrid")) {
      return "Hybrid";
    }

    // Check for both sativa and indica (hybrid)
    if (lower.includes("sativa") && lower.includes("indica")) {
      return "Hybrid";
    }

    // Known Sativa-dominant strains
    const sativaStrains = [
      "sour diesel",
      "jack herer",
      "durban poison",
      "green crack",
      "super silver haze",
      "trainwreck",
      "strawberry cough",
      "ak-47",
      "white widow",
      "blue dream",
      "pineapple express",
      "amnesia haze",
      "lemon haze",
      "tangie",
      "chocolope",
    ];

    // Known Indica-dominant strains
    const indicaStrains = [
      "granddaddy purple",
      "northern lights",
      "bubba kush",
      "purple kush",
      "afghan kush",
      "og kush",
      "blueberry",
      "grape ape",
      "blackberry kush",
      "master kush",
      "hindu kush",
      "purple urkle",
      "god's gift",
      "bubba",
      "purple",
    ];

    // Check against known strain lists
    for (const sativaStrain of sativaStrains) {
      if (lower.includes(sativaStrain)) {
        return "Sativa";
      }
    }

    for (const indicaStrain of indicaStrains) {
      if (lower.includes(indicaStrain)) {
        return "Indica";
      }
    }

    // If we can't determine, return empty (most modern strains are hybrids anyway)
    // The user mentioned that nearly every strain is a hybrid today
    return "";
  };

  const strainType = getStrainType();

  // Calculate THC percentage based on UOM
  const calculateTHCPercent = (
    value: number | undefined,
    uom: string
  ): number | null => {
    if (value === undefined || value === null) return null;

    const lowerUom = uom.toLowerCase().trim();

    // If already in percentage, return as is
    if (lowerUom === "%" || lowerUom === "percent") {
      return value;
    }

    // mg/g: THC % = THC (mg/g) ÷ 10
    if (lowerUom === "mg/g" || lowerUom === "mg per g" || lowerUom === "mg/g") {
      return value / 10;
    }

    // mg: THC % = (THC mg / product weight in mg) * 100
    // Need product weight - try to extract from netContent or use equivalenceDriedFlowerG
    if (lowerUom === "mg") {
      // Try to extract weight from netContent (e.g., "2g", "500mg")
      let weightInMg: number | null = null;
      if (netContent) {
        const netContentLower = netContent.toLowerCase();
        if (netContentLower.includes("g")) {
          const grams = parseFloat(netContentLower.replace(/[^0-9.]/g, ""));
          if (!isNaN(grams)) weightInMg = grams * 1000;
        } else if (netContentLower.includes("mg")) {
          const mg = parseFloat(netContentLower.replace(/[^0-9.]/g, ""));
          if (!isNaN(mg)) weightInMg = mg;
        }
      }
      // Fallback to equivalenceDriedFlowerG if available
      if (
        weightInMg === null &&
        equivalenceDriedFlowerG !== undefined &&
        equivalenceDriedFlowerG !== null
      ) {
        weightInMg = Number(equivalenceDriedFlowerG) * 1000;
      }
      if (weightInMg !== null && weightInMg > 0) {
        return (value / weightInMg) * 100;
      }
      // If we can't determine weight, return null
      return null;
    }

    // mg/unit: THC % = (mg per unit / unit weight in mg) * 100
    // This is complex and requires unit weight, which we may not have
    if (lowerUom === "mg/unit" || lowerUom === "mg per unit") {
      // Similar logic to mg, but per unit
      // For now, return null if we can't calculate
      return null;
    }

    // Unknown UOM, return null
    return null;
  };

  // Format THC range - returns null if no data
  const formatTHCRange = (): string | null => {
    // Check if thcPercent exists and is valid
    if (
      thcPercent !== undefined &&
      thcPercent !== null &&
      !isNaN(Number(thcPercent))
    ) {
      const percent = Number(thcPercent);
      if (percent > 0) {
        return `${percent.toFixed(1)}%`;
      }
    }

    // THC TOTAL mg/g from lab/import, stored such that % ≈ mg÷10 (same convention as Reups sheet).
    if (
      thcMg !== undefined &&
      thcMg !== null &&
      !isNaN(Number(thcMg)) &&
      Number(thcMg) > 0
    ) {
      return `${(Number(thcMg) / 10).toFixed(1)}%`;
    }

    // Check if thcMin/thcMax exist and are valid
    // If both are null/undefined, return null
    if (
      (thcMin === undefined || thcMin === null) &&
      (thcMax === undefined || thcMax === null)
    ) {
      return null;
    }

    // Try to calculate from UOM
    const thcMinPercent = calculateTHCPercent(thcMin, thcUom);
    const thcMaxPercent = calculateTHCPercent(thcMax, thcUom);

    if (thcMinPercent !== null && thcMaxPercent !== null) {
      if (Math.abs(thcMinPercent - thcMaxPercent) < 0.01) {
        // Min = Max, show single %
        return `${thcMinPercent.toFixed(1)}%`;
      } else {
        // Min < Max, show range
        return `${thcMinPercent.toFixed(1)} - ${thcMaxPercent.toFixed(1)}%`;
      }
    } else if (thcMinPercent !== null) {
      return `${thcMinPercent.toFixed(1)}%`;
    } else if (thcMaxPercent !== null) {
      return `${thcMaxPercent.toFixed(1)}%`;
    }

    // Fallback to raw values only if they're valid numbers
    if (
      thcMin !== undefined &&
      thcMin !== null &&
      !isNaN(Number(thcMin)) &&
      thcMax !== undefined &&
      thcMax !== null &&
      !isNaN(Number(thcMax))
    ) {
      const min = Number(thcMin);
      const max = Number(thcMax);
      if (Math.abs(min - max) < 0.01) {
        return `${min}%`;
      } else {
        return `${min} - ${max}%`;
      }
    } else if (
      thcMin !== undefined &&
      thcMin !== null &&
      !isNaN(Number(thcMin))
    ) {
      const min = Number(thcMin);
      if (min > 0) {
        return `${min}%`;
      }
    }

    // Return null if no valid data available
    return null;
  };

  const thcRange = formatTHCRange();

  // Format CBD range (same logic as THC) - returns null if no data
  const formatCBDRange = (): string | null => {
    // Check if cbdPercent exists and is valid
    if (
      cbdPercent !== undefined &&
      cbdPercent !== null &&
      !isNaN(Number(cbdPercent))
    ) {
      const percent = Number(cbdPercent);
      if (percent > 0) {
        return `${percent.toFixed(1)}%`;
      }
    }

    if (
      cbdMg !== undefined &&
      cbdMg !== null &&
      !isNaN(Number(cbdMg)) &&
      Number(cbdMg) > 0
    ) {
      return `${(Number(cbdMg) / 10).toFixed(1)}%`;
    }

    // Check if cbdMin/cbdMax exist and are valid
    // If both are null/undefined, return null
    if (
      (cbdMin === undefined || cbdMin === null) &&
      (cbdMax === undefined || cbdMax === null)
    ) {
      return null;
    }

    // Try to calculate from UOM
    const cbdMinPercent = calculateTHCPercent(cbdMin, cbdUom); // Reuse THC calculation logic
    const cbdMaxPercent = calculateTHCPercent(cbdMax, cbdUom);

    if (cbdMinPercent !== null && cbdMaxPercent !== null) {
      if (Math.abs(cbdMinPercent - cbdMaxPercent) < 0.01) {
        return `${cbdMinPercent.toFixed(1)}%`;
      } else {
        return `${cbdMinPercent.toFixed(1)} - ${cbdMaxPercent.toFixed(1)}%`;
      }
    } else if (cbdMinPercent !== null) {
      return `${cbdMinPercent.toFixed(1)}%`;
    } else if (cbdMaxPercent !== null) {
      return `${cbdMaxPercent.toFixed(1)}%`;
    }

    // Fallback to raw values only if they're valid numbers
    if (
      cbdMin !== undefined &&
      cbdMin !== null &&
      !isNaN(Number(cbdMin)) &&
      cbdMax !== undefined &&
      cbdMax !== null &&
      !isNaN(Number(cbdMax))
    ) {
      const min = Number(cbdMin);
      const max = Number(cbdMax);
      if (Math.abs(min - max) < 0.01) {
        return `${min}%`;
      } else {
        return `${min} - ${max}%`;
      }
    } else if (
      cbdMin !== undefined &&
      cbdMin !== null &&
      !isNaN(Number(cbdMin))
    ) {
      const min = Number(cbdMin);
      if (min > 0) {
        return `${min}%`;
      }
    }

    // Return null if no valid data available
    return null;
  };

  const cbdRange = formatCBDRange();

  const funnelSource =
    analyticsSource ??
    (homepage ? "homepage" : relatedProducts ? "related_products" : "search_grid");

  const handleAddToCart = () => {
    if (displayInStock <= 0 || !displayPrice) return;

    // Use variant parameter (product slug) for cart identification instead of packageId
    // Use parsed product name from backend (item.product.name)
    const productNameForCart = item.product.name;
    const productSlug =
      selectedSizeOption?.productSlug ||
      item.product.slug ||
      generateProductSlug({
        name: productNameForCart,
        id: displayProductId,
        brandName: item.product.brandName,
        classificationName: item.product.classificationName,
        sizeOrWeight: selectedSizeOption?.sizeOrWeight || null,
      });

    addItem(
      {
        productId: displayProductId,
        name: productNameForCart,
        catalogItemId: displayProductId,
        packageId: productSlug,
        price: displayPrice,
        actualPackageId: displayPackageId,
        inStock: displayInStock,
      } as any,
      parseInt(selectedQuantity) || 1
    );

    trackFunnelEvent({
      event: "funnel_add_to_cart",
      product_id: displayProductId,
      cart_value_bucket: getCartValueBucket(displayPrice * (parseInt(selectedQuantity) || 1)),
      source: funnelSource,
    });
  };

  const isOutOfStock = displayInStock <= 0;
  const canAddToCart = !isOutOfStock && displayPrice > 0;

  // Calculate discount percentage from original price
  // Only show discount if original price exists and is higher than current price
  // Minimum discount threshold: 5% to avoid showing tiny discounts
  const discountPercent =
    originalPrice && originalPrice > displayPrice
      ? Math.round(((originalPrice - displayPrice) / originalPrice) * 100)
      : 0;

  // Only show discount badge if discount is meaningful (>= 5%)
  const showDiscount = discountPercent >= 5;

  return (
    <div className={`group relative flex min-w-0 flex-col border border-neutral-1 bg-constant-white ${homepage ? 'rounded-2xl' : relatedProducts ? 'rounded-xl' : ''}`}>
      {/* Discount Badge */}
      {showDiscount && (
        <div className="absolute top-5 left-5 z-10 bg-yellow-50 rounded-lg px-3 py-1.5 flex items-center gap-1.5 shadow-sm">
          <FlameFilledIcon size={14} className="w-3.5 h-3.5 text-orange-500" />
          <span className="text-xs font-bold text-blue-100 uppercase">
            {discountPercent}% OFF
          </span>
        </div>
      )}

      {/* Product image, semantic link (keyboard + screen readers); favorite/color sit outside the link */}
      <div className="p-4">
        <div className="relative flex h-[165px] w-full items-center justify-center overflow-hidden rounded-xl border border-neutral-10 bg-constant-white">
          <Link
            href={productHref}
            data-no-hover
            onClick={() =>
              trackFunnelEventOnce(
                `product-click:${displayProductId}:${productHref}`,
                {
                  event: "funnel_product_click",
                  product_id: displayProductId,
                  source: funnelSource,
                },
                1200
              )
            }
            className="absolute inset-0 z-0 flex items-center justify-center rounded-xl outline-none"
            aria-label={`View ${displayProductTitle}`}
          >
            {displayHeroShotUri && !imageError ? (
              <img
                key={displayHeroShotUri}
                src={displayHeroShotUri}
                alt=""
                className="h-[165px] w-[176px] object-contain"
                onError={() => {
                  setImageError(true);
                }}
                loading="lazy"
                decoding="async"
                fetchPriority="low"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-neutral-10">
                {!placeholderError ? (
                  <img
                    src={PRODUCT_PLACEHOLDER_IMAGE}
                    alt=""
                    className="h-24 w-24 object-contain opacity-30"
                    onError={() => {
                      setPlaceholderError(true);
                    }}
                    loading="lazy"
                    decoding="async"
                    fetchPriority="low"
                  />
                ) : (
                  <svg
                    className="h-16 w-16 text-neutral-50"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                )}
              </div>
            )}

            {isOutOfStock && (
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-xl bg-black/50">
                <span className="rounded bg-neutral-80 px-3 py-1 text-sm font-semibold text-constant-white">
                  Out of Stock
                </span>
              </div>
            )}
          </Link>

          <div className="absolute top-2 right-2 z-30">
            <FavoriteButton productId={item.productId} size={16} className="shadow-sm" />
          </div>

          {/* Color Selection Dropdown for Clothing Products - Positioned at bottom-right of image */}
          {(() => {
            const classification = item.product.classificationName || '';
            const isAccessories = classification && classification.toLowerCase().includes('accessories');
            const isClothing = classification && (
              classification.toLowerCase().includes('clothing') ||
              classification.toLowerCase().includes('merch') ||
              classification.toLowerCase().includes('apparel')
            ) && !isAccessories; // Exclude accessories from clothing
            
            if (isClothing && clothingVariants && clothingVariants.colors.length > 0) {
              // Get groupProducts for raw stock values
              const groupProducts = (item as any).groupProducts || [];
              
              // Filter colors to only show those with available sizes (after stock-1 rule)
              // Also check if there are any size options available for that color
              const availableColors = clothingVariants.colors.filter((color) => {
                const colorProducts = clothingVariants.variantsByColor.get(color) || [];
                
                // Create a Set of productIds and packageIds for this color
                const colorProductIds = new Set<string>();
                const colorPackageIds = new Set<string>();
                colorProducts.forEach(p => {
                  if (p.productId) colorProductIds.add(p.productId);
                  if (p.packageId) colorPackageIds.add(p.packageId);
                });
                
                // Check if any size options match this color's products
                const hasMatchingSizeOptions = availableSizeOptions.some((opt: SizeOption) =>
                  (opt.productId && colorProductIds.has(opt.productId)) ||
                  (opt.packageId && colorPackageIds.has(opt.packageId))
                );
                
                // If no matching size options, hide this color
                if (!hasMatchingSizeOptions) return false;
                
                // Check if any product has stock >= 2 (so stock-1 >= 1)
                return colorProducts.some(p => {
                  // Get raw stock from groupProducts if available, otherwise use p.inStock
                  const rawStock = groupProducts.find((gp: any) => 
                    gp.productId === p.productId || gp.packageId === p.packageId
                  )?.inStock ?? p.inStock ?? 0;
                  
                  // Apply stock-1 rule: if raw stock is 2, available stock is 1
                  const displayStock = Math.max(0, rawStock - 1);
                  return displayStock > 0;
                });
              });
              
              // Don't render if no colors are available
              if (availableColors.length === 0) return null;
              
              const selectedColorData = selectedColor 
                ? clothingVariants.variantsByColor.get(selectedColor)?.[0]
                : null;
              const selectedColorHex = selectedColor ? getColorHex(selectedColor) : '#CCCCCC';
              
              return (
                <div className="absolute bottom-2 right-2 z-20 color-dropdown-container">
                  <div className="relative">
                    {/* Dropdown Button */}
                    <Button
                      raw
                      type="button"
                      onClick={() => setIsColorDropdownOpen(!isColorDropdownOpen)}
                      className="rounded-lg border border-neutral-10 bg-white/90 p-1.5 shadow-sm transition-colors"
                      aria-label="Select color"
                      aria-expanded={isColorDropdownOpen}
                    >
                      <div
                        className="h-5 w-5 rounded-full border-2 border-blue-100"
                        style={{ backgroundColor: selectedColorHex }}
                      />
                    </Button>

                    {/* Dropdown Menu - Horizontal */}
                    {isColorDropdownOpen && (
                      <div className="absolute bottom-full right-10 top-0 mb-2 bg-white border border-neutral-10 rounded-lg shadow-lg z-20 p-2 h-9">
                        <div className="flex gap-2">
                          {availableColors.map((color) => {
                            const isSelectedColor = selectedColor === color;
                            const colorOptions = clothingVariants.variantsByColor.get(color) || [];
                            const colorHex = getColorHex(color);
                            
                            return (
                              <Button
                                raw
                                key={color}
                                type="button"
                                onClick={() => {
                                  setSelectedColor(color);
                                  setIsColorDropdownOpen(false);
                                }}
                                className={`h-5 w-5 flex-shrink-0 rounded-full border-2 transition-all ${
                                  isSelectedColor
                                    ? "border-blue-100 shadow-lg scale-110"
                                    : "border-neutral-20 hover:scale-105 hover:border-blue-100"
                                }`}
                                style={{ backgroundColor: colorHex }}
                                title={color}
                                aria-label={`Select ${color} color`}
                              />
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            }
            return null;
          })()}
        </div>
      </div>

      {/* Product Info */}
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden p-4 pt-0">
        {/* Brand, same single-line ellipsis as title (was wrapping on narrow cards) */}
        {brand && (
          <div
            className="min-w-0 w-full truncate text-body-14-desktop font-medium text-text-secondary"
            title={brand}
          >
            {brand}
          </div>
        )}

        {/* Product name, same PDP URL as image; visible focus ring for keyboard users */}
        <h3 className="mt-1 mb-2 min-w-0 w-full text-body-16-desktop font-medium">
          <Link
            href={productHref}
            onClick={() =>
              trackFunnelEventOnce(
                `product-click:${displayProductId}:${productHref}`,
                {
                  event: "funnel_product_click",
                  product_id: displayProductId,
                  source: funnelSource,
                },
                1200
              )
            }
            className="block truncate text-text-primary outline-none"
            title={displayProductTitle}
          >
            {displayProductTitle}
          </Link>
        </h3>

        {/* Pills */}
        <div className="mb-2 flex items-center gap-2 flex-wrap">
          {item.lotCode && (
            <span className="rounded-md bg-neutral-5 px-1.5 py-1 text-body-12-desktop font-medium text-text-secondary">
              Lot {item.lotCode}
            </span>
          )}
          {strainType && (
            <span className="rounded-md bg-neutral-5 px-1.5 py-1 text-body-12-desktop font-medium text-text-secondary">
              {strainType}
            </span>
          )}
          {productType && (
            <span className="rounded-md bg-neutral-5 px-1.5 py-1 text-body-12-desktop font-medium text-text-secondary">
              {productType}
            </span>
          )}
        </div>

        {/* THC / CBD */}
        <div className="mb-2 text-body-14-desktop font-medium text-text-secondary">
          <span>THC: {thcRange || "N/A"}</span>
          <span>{" · "}</span>
          <span>CBD: {cbdRange || "N/A"}</span>
        </div>

        {/* Price Section */}
        <div className="mb-2">
          <div className="flex items-baseline gap-2">
            <span className="text-body-18 font-bold text-text-primary">
              ${displayPrice.toFixed(2)}
            </span>
            {showDiscount && originalPrice && (
              <span className="text-body-14-desktop font-medium text-text-tertiary line-through">
                ${originalPrice.toFixed(2)}
              </span>
            )}
          </div>
        </div>

        {/* Add to Cart Button */}
        <div className="mt-auto">
          {/* Size Options - Show if variants available */}
          {(() => {
            // Show options even if there's only 1 option
            if (displaySizeOptions.length === 0) {
              return null;
            }

            // Segmented control, single bordered pill, vertical dividers, light fill on selected (see design)
            // The scroll arrows render only when the size row overflows, and
            // when they do, BOTH are always present at fixed positions (the
            // unavailable direction is disabled, not removed) with constant
            // padding, so clicking an arrow never shifts the arrows or chips.
            const sizesScrollable = canScrollSizesLeft || canScrollSizesRight;
            return (
              <div className="mb-2 w-full">
                <div className="relative w-full">
                  {sizesScrollable ? (
                    <>
                      <Button
                        raw
                        type="button"
                        onClick={() => sizeScrollRef.current?.scrollBy({ left: -220, behavior: 'smooth' })}
                        disabled={!canScrollSizesLeft}
                        className="absolute left-1 top-1/2 z-10 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full border border-neutral-10 bg-constant-white shadow-sm transition-all hover:border-neutral-70 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-neutral-10"
                        aria-label="Scroll sizes left"
                      >
                        <ChevronLeftIcon size={16} color="currentColor" className="text-blue-100" />
                      </Button>
                      <Button
                        raw
                        type="button"
                        onClick={() => sizeScrollRef.current?.scrollBy({ left: 220, behavior: 'smooth' })}
                        disabled={!canScrollSizesRight}
                        className="absolute right-1 top-1/2 z-10 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full border border-neutral-10 bg-constant-white shadow-sm transition-all hover:border-neutral-70 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-neutral-10"
                        aria-label="Scroll sizes right"
                      >
                        <ChevronRightIcon size={16} color="currentColor" className="text-blue-100" />
                      </Button>
                    </>
                  ) : null}

                  <div
                    ref={sizeScrollRef}
                    className={`scrollbar-hide flex scroll-smooth overflow-x-auto ${
                      sizesScrollable ? 'px-10' : ''
                    }`}
                  >
                    <div
                      role="group"
                      aria-label="Size or weight"
                      className="inline-flex min-w-full shrink-0 overflow-hidden rounded-xl border border-neutral-10 bg-constant-white"
                    >
                      {displaySizeOptions.map((sizeOption, index) => {
                        const sizeLabel = sizeOption.sizeOrWeight || sizeOption.netContent || 'Default';
                        const packageMatches = selectedSizeOption?.packageId === sizeOption.packageId;
                        const slugMatches =
                          selectedSizeOption?.productSlug &&
                          sizeOption.productSlug &&
                          selectedSizeOption.productSlug === sizeOption.productSlug;
                        const isSelected =
                          packageMatches &&
                          (slugMatches || (!selectedSizeOption?.productSlug && !sizeOption.productSlug));
                        const isOutOfStock = sizeOption.inStock < 1;
                        const uniqueKey = `size-${sizeOption.productId || 'no-id'}-${sizeOption.packageId || 'no-pkg'}-${sizeOption.productSlug || 'no-slug'}-${index}`;

                        return (
                          <Button
                            raw
                            key={uniqueKey}
                            type="button"
                            onClick={() => !isOutOfStock && setSelectedSizeOption(sizeOption)}
                            disabled={isOutOfStock}
                            aria-pressed={isSelected}
                            className={[
                              'relative min-w-[4rem] flex-1 basis-0 px-2 py-2.5 text-center text-sm font-semibold text-blue-100 transition-colors select-none',
                              '',
                              'disabled:cursor-not-allowed disabled:opacity-50',
                              index > 0 ? 'border-l border-neutral-10' : '',
                              isSelected
                                ? 'bg-blue-5'
                                : isOutOfStock
                                  ? 'bg-neutral-5 text-neutral-40'
                                  : 'bg-constant-white',
                            ].join(' ')}
                          >
                            <span className="block truncate">{sizeLabel}</span>
                          </Button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
          {/* Product compare feature removed */}
          <Button
            type="button"
            variant="primary"
            size="lg"
            onClick={handleAddToCart}
            disabled={!canAddToCart}
            className="w-full rounded-xl"
          >
            Add to cart
          </Button>
        </div>
      </div>
    </div>
  );
}
